import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const ImageUpload = ({ onUpload, currentImage, label = "Image", aspectRatio = "square" }) => {
    const [preview, setPreview] = useState(currentImage || null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const API_URL = process.env.REACT_APP_BACKEND_URL;

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Format non supporté. Utilisez JPG, PNG ou WebP');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 5MB)');
            return;
        }

        // Show preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreview(reader.result);
        };
        reader.readAsDataURL(file);

        // Upload file
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(`${API_URL}/api/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Erreur lors de l\'upload');
            }

            const data = await response.json();
            toast.success('Image uploadée avec succès');
            onUpload(data.url);
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Erreur lors de l\'upload de l\'image');
            setPreview(currentImage || null);
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        setPreview(null);
        onUpload(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const aspectClasses = {
        square: 'aspect-square',
        video: 'aspect-video',
        portrait: 'aspect-[3/4]',
    };

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {label}
            </label>
            
            <div className={`relative ${aspectClasses[aspectRatio] || 'aspect-square'} w-full max-w-md`}>
                {preview ? (
                    <div className="relative w-full h-full group">
                        <img
                            src={preview.startsWith('http') || preview.startsWith('/') ? `${API_URL}${preview}` : preview}
                            alt="Preview"
                            className="w-full h-full object-cover rounded-xl border-2 border-slate-200 dark:border-slate-700"
                        />
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full h-full border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-950/20 transition-all group"
                    >
                        {uploading ? (
                            <div className="flex flex-col items-center gap-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-500 border-t-transparent" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">Upload en cours...</span>
                            </div>
                        ) : (
                            <>
                                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full group-hover:bg-green-100 dark:group-hover:bg-green-900/30 transition-colors">
                                    <ImageIcon className="w-8 h-8 text-slate-400 group-hover:text-green-600 dark:group-hover:text-green-400" />
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                        Cliquez pour uploader
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        JPG, PNG, WebP - Max 5MB
                                    </p>
                                </div>
                            </>
                        )}
                    </button>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
            />
        </div>
    );
};

export default ImageUpload;
